/**
 * Budget & Draws design tokens + number formatters.
 *
 * ONE place for the tab's palette so the whole surface (overview card, workbook,
 * draws ledger, new-draw sidebar) stays in step — and so a brand swap is a
 * one-line change here rather than a sweep.
 *
 * NOTE on the accent: `#0E8FA8` is the panel design's teal, already shipped in
 * DrilldownSidebarAddDraw. It is NOT one of the Eastview brand teals on the
 * CLAUDE.md #2 recolor table (#2196B5 / #14506b / #1693b3) — it's this surface's
 * own accent. To move the tab onto Lendmarq brand blue, change ACCENT /
 * ACCENT_INK / ACCENT_TINT / ACCENT_TINT_2 here and nothing else.
 *
 * Numbers use a system monospace stack (no webfont) so dollar columns align
 * without adding a network dependency to the drilldown.
 */

export const T = {
  ink: "#182430",
  ink2: "#31404E",
  muted: "#64748B",
  faint: "#94A3B8",

  card: "#FFFFFF",
  line: "#E5EAEF",
  line2: "#EDF1F4",
  subtle: "#FBFCFD",
  computed: "#F6FAFB",

  accent: "#0E8FA8",
  accentInk: "#0A6B7E",
  accentTint: "#EAF6F8",
  accentTint2: "#D6EDF2",

  green: "#178A50",
  greenTint: "#EDF8F1",
  greenTint2: "#E7F4EC",
  greenLine: "#CBE8D6",

  amber: "#B97318",
  amberTint: "#FCF4E6",
  amberLine: "#F2E2C4",
  amberInk: "#7A5212",

  red: "#C0392B",

  /**
   * ONE size for every data cell in the workbook — item name, chips, numeric
   * inputs, computed cells, the reimburse flag and the detail-row fields all
   * read at this size. Column headers are the only exception (they're labels,
   * not data). Mixed cell sizes made the table look like several tables.
   */
  cellFont: 12,

  radius: "10px",
  radiusSm: "7px",
  shadow: "0 1px 2px rgba(24,36,48,.05), 0 4px 16px rgba(24,36,48,.05)",
  mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
};

/** Card shell shared by the overview, the workbook and the draws sections. */
export const cardSx = {
  bgcolor: T.card,
  border: `1px solid ${T.line}`,
  borderRadius: T.radius,
  boxShadow: T.shadow,
  overflow: "hidden",
};

/** Small uppercase label used for every stat/total caption on the tab. */
export const labelSx = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: T.faint,
  lineHeight: 1.3,
};

const toNum = (v) => {
  if (v == null || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Placeholder for an absent value. */
export const EMPTY = "-";

/** Whole-dollar display. The tab never shows cents. */
export const money = (v) =>
  v == null || v === "" ? EMPTY : `$${Math.round(toNum(v)).toLocaleString("en-US")}`;

/** 0 to 1 fraction to percent string. LOS stores fractions everywhere. */
export const pct = (v, digits = 1) =>
  v == null || v === "" ? EMPTY : `${(toNum(v) * 100).toFixed(digits)}%`;

export const num = toNum;

/** Draw advance math, mirrored from servicing.DrawDisbursement. */
export const eligibleAdvance = (percentComplete, totalHoldback, totalDrawn) =>
  Math.max(0, toNum(percentComplete) * toNum(totalHoldback) - toNum(totalDrawn));

/**
 * "How much can we release next?" — computed once in the tab shell so the
 * capital band and the draws card can never disagree about it.
 *
 * Once draws exist the basis switches to the last INSPECTED percent: the
 * inspected figure, not the sheet's self-reported completion, governs the
 * advance.
 */
export const buildEligible = ({ draws, weightedPercentComplete, rollup }) => {
  const list = draws || [];
  const lastInspected = list.length ? toNum(list[list.length - 1]?.percentComplete) : null;
  const basis = lastInspected ?? toNum(weightedPercentComplete);
  const fromInspection = lastInspected != null;
  return {
    basis,
    fromInspection,
    amount: eligibleAdvance(basis, rollup?.totalHoldback, rollup?.totalDrawnToDate),
    /** Parts, so each surface can render its own emphasis. */
    lead: fromInspection ? "At the last inspected" : "At the sheet's weighted",
    tail: fromInspection
      ? "is eligible for the next draw"
      : "is eligible to advance",
    digits: fromInspection ? 0 : 1,
  };
};
